import {
  ChangeDetectionStrategy,
  Component,
  EnvironmentInjector,
  inject,
  Input,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AbilityServiceSignal } from '@casl/angular';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { select, Store } from '@ngxs/store';
import { captureException } from '@sentry/capacitor';
import { AuthorizationModule } from '@wilson/authorization';
import {
  Activity,
  ActivityTemplate,
  RoleAction,
  RolePermissionSubject,
  Shift,
  ShiftTemplate,
} from '@wilson/interfaces';
import { LocalizationState } from '@wilson/localization';
import { EventCategory } from '@wilson/matomo';
import { createFullActivityTemplateObject } from '@wilson/non-domain-specific/activities-helpers/services';
import { SaveAsTemplateDialogComponent } from '@wilson/non-domain-specific/shift-helpers/core';
import { ShiftsService } from '@wilson/shifts';
import { formatISO } from 'date-fns';
import { NzDropdownModule } from 'ng-zorro-antd/dropdown';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { MatomoTracker } from 'ngx-matomo-client';

@Component({
  selector: 'wilson-save-as-template-action',
  imports: [
    TranslatePipe,
    NzDropdownModule,
    AuthorizationModule,
    NzTooltipModule,
  ],
  templateUrl: './save-as-template-action.component.html',
  styleUrl: './save-as-template-action.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SaveAsTemplateActionComponent {
  private readonly store = inject(Store);
  private readonly nzModalService = inject(NzModalService);
  private readonly translate = inject(TranslateService);
  private readonly tracker = inject(MatomoTracker);
  private readonly shiftsService = inject(ShiftsService);
  private readonly nzMessageService = inject(NzMessageService);
  private readonly injector = inject(EnvironmentInjector);

  @Input()
  disabled = false;

  @Input({ required: true }) shift!: Shift;
  // A signal, so the OnPush modal footer re-evaluates its `disabled` callback.
  readonly submitted = signal(false);

  protected RoleAction = RoleAction;
  protected RolePermissionSubject = RolePermissionSubject;
  protected can = inject(AbilityServiceSignal).can;

  protected saveAsTemplate(isDisabled: boolean): Promise<void> | void {
    if (!isDisabled) {
      const userId = this.store.selectSnapshot((state) => state.auth.userId);
      const modal: NzModalRef = this.nzModalService.create({
        nzTitle: this.translate.instant('shift.page.save_as_template'),
        nzContent: SaveAsTemplateDialogComponent,
        nzWidth: '500px',
        nzFooter: [
          {
            label: this.translate.instant('general.cancel'),
            type: 'default',
            onClick: (): void => {
              modal.destroy();
            },
          },
          {
            label: this.translate.instant('general.accept'),
            type: 'primary',
            disabled: (componentInstance): boolean => {
              return (
                this.submitted() ||
                (
                  componentInstance as SaveAsTemplateDialogComponent
                ).isFormInvalid()
              );
            },
            onClick: async (componentInstance): Promise<void> => {
              await this.accept(
                (componentInstance as SaveAsTemplateDialogComponent).form,
                userId,
              );
              modal.destroy();
            },
          },
        ],
      });

      if (modal.componentInstance) {
        modal.componentInstance.shift = this.shift;
        modal.componentInstance.userId = userId;
      }

      return modal.result;
    }
  }

  private async accept(form: FormGroup, userId: string): Promise<void> {
    this.submitted.set(true);
    const shiftTemplate: ShiftTemplate = form.value;
    const activities = this.shift.activities;
    delete shiftTemplate.organizationalUnit;
    shiftTemplate.shiftCategoryId = this.shift?.shiftCategoryId;
    shiftTemplate.createdById = userId;

    shiftTemplate.activityTemplates = activities
      ? this.prepareActivityTemplates(activities)
      : [];

    try {
      await this.shiftsService.saveShiftAsTemplate(
        this.shift.id as string,
        shiftTemplate,
      );

      this.nzMessageService.success(
        this.translate.instant('shift.template.save_success', {
          name: shiftTemplate.name,
        }),
      );
    } catch (error) {
      captureException(error);
      this.submitted.set(false);
      this.nzMessageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
    }
    this.tracker.trackEvent(EventCategory.Shift, 'save_shift_template');
  }

  private prepareActivityTemplates(activities: Activity[]): ActivityTemplate[] {
    const defaultTimezone = runInInjectionContext(this.injector, () =>
      select(LocalizationState.getDefaultTimezone),
    );
    const activityTemplates: ActivityTemplate[] = [];
    if (activities) {
      for (const activity of activities) {
        activityTemplates.push({
          ...createFullActivityTemplateObject({
            defaultTimezone: defaultTimezone(),
            partialActivityTemplate: {
              id: activity.id as string,
              name: activity.name,
              startDatetime: formatISO(new Date(activity.startDatetime)),
              endDatetime: formatISO(new Date(activity.endDatetime)),
              activityCategoryId: activity.activityCategoryId as string,
              startLocationId: activity.startLocationId as string,
              endLocationId: activity.endLocationId as string,
              agreementId: activity.agreementId as string,
              professionId: activity.professionId as string,
              viaLocationIds: activity.viaLocationIds.length
                ? activity.viaLocationIds
                : [],

              numberOfWagons: activity.numberOfWagons || null,
              tonnage: activity.tonnage || null,
              travelDirection: activity.travelDirection || null,
              vehicleEmissionClassId: activity.vehicleEmissionClassId || null,
              orderNumber: activity.orderNumber || null,
              wagonNumber: activity.wagonNumber || null,
              locomotiveNumber: activity.locomotiveNumber || null,
              ...(activity.validateRoute != null && {
                validateRoute: activity.validateRoute,
              }),
              startTimeZone: defaultTimezone(),
              endTimeZone: defaultTimezone(),
            },
          }),
        });
      }
    }
    return activityTemplates;
  }
}
